import React from 'react';
import { Link } from 'react-router-dom';
import SeoPageLayout, { useSeo, seoTextStyles as s } from '../../components/seo/SeoPageLayout';

export default function FreeSpinningRecordVideoMaker() {
  useSeo(
    'Free Spinning Record Video Maker — Waxify',
    'Turn any album art and audio track into a spinning-disc video for free, right in your browser. No watermark, no software to install, no account required.'
  );

  return (
    <SeoPageLayout>
      <h1 className={s.h1}>Free Spinning Record Video Maker</h1>
      <p className={s.lead}>
        A spinning record video — album art rotating on a virtual turntable while a
        track plays underneath — has become one of the simplest, most effective ways
        for musicians and labels to promote a song on Instagram, TikTok, and YouTube.
        Waxify makes one of these videos in a couple of minutes, for free, with nothing
        to install and no watermark on the export.
      </p>

      <h2 className={s.h2}>Why a spinning record video works</h2>
      <p className={s.p}>
        Static cover-art posts get scrolled past. A subtle spin adds motion, which is
        what stops a thumb mid-scroll on a feed built entirely around video. It also
        does something a static image can't: it looks like a physical record playing,
        which reads as more "real" and more collectible than a flat square of art,
        even when the source is a digital single.
      </p>

      <h2 className={s.h2}>What you get for free</h2>
      <ul className={s.ul}>
        <li>Upload your own disc art (or use one of the built-in shapes) and a background image</li>
        <li>Realistic vinyl grooves, label, and spindle hole, or a plain shape — your choice</li>
        <li>Adjustable RPM from a crawl up to 300, plus a one-click reverse-spin toggle</li>
        <li>Frame-accurate audio trimming with a zoomable waveform</li>
        <li>Square, portrait, and landscape export presets sized for Instagram, TikTok, and YouTube</li>
        <li>A server-rendered MP4 — not a screen recording — so the output is sharp and audio-synced</li>
      </ul>

      <h2 className={s.h2}>How it's free</h2>
      <p className={s.p}>
        There's no premium tier gating export quality or adding a watermark. You upload
        your files, set up the disc the way you want it in the live preview, and export.
        The video renders on our servers with FFmpeg and comes back as a clean MP4 you
        can post immediately.
      </p>

      <h2 className={s.h2}>Who this is for</h2>
      <p className={s.p}>
        Independent musicians teasing a new single, labels announcing a vinyl repress,
        DJs previewing a set, and podcasters who only have an audio file but need
        something visual for platforms that don't support audio-only posts — anyone
        who has a track and a piece of art and fifteen minutes.
      </p>

      <p className={s.p}>
        Ready to try it? <Link to="/" className="text-orange-600 hover:underline font-medium">Open the editor</Link> and
        upload your first disc — or read the full <Link to="/how-to-make-a-spinning-record-video" className="text-orange-600 hover:underline font-medium">step-by-step guide</Link> first.
      </p>
    </SeoPageLayout>
  );
}
